import Phaser from 'phaser';
import { dustHudWorldTarget } from '../../hud-target';
import { addDust, addInventoryItem, grantOneBreakDust } from '../../player-state';

/** Offline fallback when no server pending loot is available. */
export const OFFLINE_BREAK_DUST_MIN = 2;
export const OFFLINE_BREAK_DUST_RANGE = 4;

const DROP_DEPTH = 15;
const DUST_SIZE = 240;
const ITEM_SIZE = 320;
const STAGGER_MS = 80;
const AUTO_COLLECT_MS = 5000;
const SCATTER_RADIUS_MIN = 180;
const SCATTER_RADIUS_MAX = 480;
const GROUND_DROP_Y = 320;

type DustDrop = {
  sprite: Phaser.GameObjects.Image;
  fromServerGrant: boolean;
  collected: boolean;
  ready: boolean;
};

type ItemDrop = {
  sprite: Phaser.GameObjects.Image;
  itemName: string;
  collected: boolean;
  ready: boolean;
};

function itemTexture(name: string) {
  const map: Record<string, string> = {
    Stone: 'drop-stone',
    Leather: 'drop-leather',
    Claws: 'drop-claws',
    'Pink Fur': 'drop-pink-fur',
  };
  return map[name] ?? 'drop-stone';
}

function dustTexture(index: number) {
  return `dust-drop-${(index % 5) + 1}`;
}

export function createGeodeDrops(scene: Phaser.Scene) {
  const dustDrops: DustDrop[] = [];
  const itemDrops: ItemDrop[] = [];

  function removeDust(entry: DustDrop) {
    const i = dustDrops.indexOf(entry);
    if (i >= 0) dustDrops.splice(i, 1);
    entry.sprite.destroy();
  }

  function removeItem(entry: ItemDrop) {
    const i = itemDrops.indexOf(entry);
    if (i >= 0) itemDrops.splice(i, 1);
    entry.sprite.destroy();
  }

  function applyDustReward(fromServerGrant: boolean) {
    if (fromServerGrant) grantOneBreakDust();
    else addDust(1);
  }

  function flyToHud(sprite: Phaser.GameObjects.Image, onDone: () => void) {
    const scrollX = scene.cameras.main.scrollX;
    const target = dustHudWorldTarget(scrollX);
    scene.tweens.killTweensOf(sprite);
    scene.tweens.add({
      targets: sprite,
      x: target.x,
      y: target.y,
      scaleX: 0.35,
      scaleY: 0.35,
      alpha: 0.35,
      duration: 550,
      ease: 'Cubic.easeIn',
      onComplete: onDone,
    });
  }

  function collectDust(entry: DustDrop) {
    if (entry.collected || !entry.ready) return;
    entry.collected = true;
    entry.sprite.disableInteractive();
    flyToHud(entry.sprite, () => {
      applyDustReward(entry.fromServerGrant);
      removeDust(entry);
    });
  }

  function collectItem(entry: ItemDrop) {
    if (entry.collected || !entry.ready) return;
    entry.collected = true;
    entry.sprite.disableInteractive();
    flyToHud(entry.sprite, () => {
      addInventoryItem(entry.itemName, 1);
      removeItem(entry);
    });
  }

  function scheduleAutoCollect(entry: DustDrop | ItemDrop, collect: () => void) {
    scene.time.delayedCall(AUTO_COLLECT_MS, () => {
      if (!entry.collected && entry.ready) collect();
    });
  }

  /** Scatter one dust piece: geode center → arc → floor. */
  function spawnDustPiece(
    cx: number,
    cy: number,
    slotIndex: number,
    totalCount: number,
    fromServerGrant: boolean,
  ) {
    const tex = dustTexture(slotIndex);
    if (!scene.textures.exists(tex)) return;

    const slotAngle = (slotIndex / totalCount) * Math.PI * 2;
    const angle = slotAngle + (Math.random() - 0.5) * 0.55;
    const radius = SCATTER_RADIUS_MIN + Math.random() * (SCATTER_RADIUS_MAX - SCATTER_RADIUS_MIN);
    const landX = cx + Math.cos(angle) * radius;
    const landY = cy + GROUND_DROP_Y + Math.sin(angle) * radius * 0.22;
    const peakX = cx + Math.cos(angle) * radius * 0.35;
    const peakY = cy - 80 - Math.random() * 120;
    const spin = (Math.random() > 0.5 ? 1 : -1) * (180 + Math.random() * 220);

    const sprite = scene.add
      .image(cx, cy, tex)
      .setDisplaySize(DUST_SIZE, DUST_SIZE)
      .setDepth(DROP_DEPTH + slotIndex * 0.02)
      .setAlpha(0.85)
      .setScale(0.2)
      .setAngle(0);

    const entry: DustDrop = { sprite, fromServerGrant, collected: false, ready: false };
    dustDrops.push(entry);

    const delay = slotIndex * STAGGER_MS;
    const duration = 750 + Math.random() * 250;

    scene.tweens.add({
      targets: sprite,
      delay,
      duration: duration * 0.42,
      x: peakX,
      y: peakY,
      scaleX: 1.05,
      scaleY: 1.05,
      angle: spin * 0.4,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: sprite,
          duration: duration * 0.58,
          x: landX,
          y: landY,
          scaleX: 1,
          scaleY: 1,
          angle: spin,
          ease: 'Cubic.easeIn',
          onComplete: () => {
            entry.ready = true;
            sprite.setInteractive({ useHandCursor: true, pixelPerfect: false });
            scheduleAutoCollect(entry, () => collectDust(entry));
          },
        });
      },
    });
  }

  /** Dust pieces on geode break — count comes from server PendingDrops or offline roll. */
  function spawnDustBurst(
    cx: number,
    cy: number,
    { count, fromServerGrant = false }: { count: number; fromServerGrant?: boolean },
  ) {
    const n = Math.max(0, Math.floor(count));
    if (n <= 0) return;
    for (let i = 0; i < n; i++) {
      spawnDustPiece(cx, cy, i, n, fromServerGrant);
    }
  }

  function spawnItemPiece(cx: number, cy: number, itemName: string, slotIndex: number) {
    const tex = itemTexture(itemName);
    if (!scene.textures.exists(tex)) return;

    const angle = Math.random() * Math.PI * 2;
    const radius = SCATTER_RADIUS_MIN + Math.random() * (SCATTER_RADIUS_MAX - SCATTER_RADIUS_MIN);
    const landX = cx + Math.cos(angle) * radius;
    const landY = cy + GROUND_DROP_Y + Math.sin(angle) * radius * 0.22;
    const peakX = cx + Math.cos(angle) * radius * 0.3;
    const peakY = cy - 60 - Math.random() * 100;

    const sprite = scene.add
      .image(cx, cy, tex)
      .setDisplaySize(ITEM_SIZE, ITEM_SIZE)
      .setDepth(DROP_DEPTH + 0.5 + slotIndex * 0.02)
      .setAlpha(0.9)
      .setScale(0.2);

    const entry: ItemDrop = { sprite, itemName, collected: false, ready: false };
    itemDrops.push(entry);

    const delay = slotIndex * STAGGER_MS;
    const duration = 800 + Math.random() * 200;

    scene.tweens.add({
      targets: sprite,
      delay,
      duration: duration * 0.4,
      x: peakX,
      y: peakY,
      scaleX: 1,
      scaleY: 1,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: sprite,
          duration: duration * 0.6,
          x: landX,
          y: landY,
          ease: 'Cubic.easeIn',
          onComplete: () => {
            entry.ready = true;
            sprite.setInteractive({ useHandCursor: true, pixelPerfect: false });
            scheduleAutoCollect(entry, () => collectItem(entry));
          },
        });
      },
    });
  }

  function spawnItems(cx: number, cy: number, items: string[]) {
    items.forEach((name, i) => spawnItemPiece(cx, cy, name, i));
  }

  function dropHitRadius(sprite: Phaser.GameObjects.Image) {
    return Math.max(sprite.displayWidth, sprite.displayHeight) * 0.62;
  }

  function pointerOnDrop(sprite: Phaser.GameObjects.Image, pointer: Phaser.Input.Pointer) {
    const bounds = sprite.getBounds();
    if (bounds.contains(pointer.worldX, pointer.worldY)) return true;

    const hits = scene.input.hitTestPointer(pointer);
    if (hits[0] === sprite || hits.includes(sprite)) return true;

    return (
      Phaser.Math.Distance.Between(pointer.worldX, pointer.worldY, sprite.x, sprite.y) <=
      dropHitRadius(sprite)
    );
  }

  function pointerAtDrop(pointer: Phaser.Input.Pointer) {
    const entries = [...itemDrops, ...dustDrops]
      .filter((entry) => entry.sprite.active)
      .sort((a, b) => b.sprite.depth - a.sprite.depth);

    return entries.some((entry) => pointerOnDrop(entry.sprite, pointer));
  }

  /** Collect topmost ready drop under the pointer; always blocks geode when over a drop. */
  function tryPickup(pointer: Phaser.Input.Pointer) {
    if (!pointerAtDrop(pointer)) return false;

    const entries = [...itemDrops, ...dustDrops]
      .filter((entry) => entry.sprite.active)
      .sort((a, b) => b.sprite.depth - a.sprite.depth);

    for (const entry of entries) {
      if (!pointerOnDrop(entry.sprite, pointer)) continue;
      if (!entry.collected && entry.ready) {
        if ('fromServerGrant' in entry) collectDust(entry);
        else collectItem(entry);
      }
      return true;
    }
    return true;
  }

  function clear() {
    for (const entry of [...dustDrops]) removeDust(entry);
    for (const entry of [...itemDrops]) removeItem(entry);
  }

  return { spawnDustBurst, spawnItems, pointerAtDrop, tryPickup, clear };
}

export type GeodeDrops = ReturnType<typeof createGeodeDrops>;
